import type { CSSProperties, ReactNode } from 'react'
import type { MemberStatus } from '../lib/api'

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>
}

export function Card({
  children,
  style,
}: {
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <div className="card" style={style}>
      {children}
    </div>
  )
}

/**
 * Status pill. Colour alone never carries the meaning — the label is always
 * present next to the dot, which is what makes this readable to someone with a
 * colour vision deficiency (WCAG 2.1 AA, TECHNICAL-REQUIREMENTS.md §5).
 */
export function StatusPill({ status }: { status: MemberStatus }) {
  const label =
    status === 'approved'
      ? 'Approved'
      : status === 'flagged'
        ? 'Flagged'
        : status === 'rejected'
          ? 'Rejected'
          : status === 'suspended'
            ? 'On hold'
            : status === 'draft'
              ? 'Draft'
              : 'Pending'

  return (
    <span className="pill">
      <span className={`dot dot-${status === 'draft' ? 'pending' : status}`} />
      {label}
    </span>
  )
}

export function Kpi({
  label,
  value,
  note,
  noteColor,
}: {
  label: string
  value: string | number
  note?: string
  noteColor?: string
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {note && (
        <div className="kpi-note" style={noteColor ? { color: noteColor } : undefined}>
          {note}
        </div>
      )}
    </div>
  )
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div className="error-box" role="alert">
      {children}
    </div>
  )
}

export function WarnBox({ children }: { children: ReactNode }) {
  return <div className="warn-box">{children}</div>
}

export function Empty({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p className="small" style={{ maxWidth: 420, margin: '8px auto 0' }}>
        {body}
      </p>
      {action && <div style={{ marginTop: 22 }}>{action}</div>}
    </div>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <div className="small">{label ?? 'Loading…'}</div>
    </div>
  )
}

/** Labelled progress bar for the register-by-house breakdown. */
export function Bar({ fraction }: { fraction: number }) {
  const pct = Math.max(0, Math.min(1, fraction)) * 100
  return (
    <div className="bar-track">
      <div className="bar-fill" style={{ width: `${pct}%` }} />
    </div>
  )
}
